import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouterState } from 'connected-react-router';

import { BoardStatus, boardState, setStatus } from 'common/modules/app/board';
import { GlossaryState } from 'common/modules/app/glossaries';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import { Glossary } from 'common/utils/glossary';
import {
  PurchaseInformation,
  PurchaseInitialState
} from 'common/modules/domain/purchase';
import {
  TerminalState,
  changeSetting,
  TerminalMap,
  GET_TERMINALS_ACTIVATION_ID
} from 'common/modules/domain/terminal';
import { AppState } from 'pages';
import { TerminalBoard } from 'pages/board/components/TerminalBoard';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const terminalState = useSelector<AppState, TerminalState>(
    (state) => state.domain.terminal
  );
  const purchaseState = useSelector<AppState, PurchaseInitialState>(
    (state) => state.domain.purchase
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );

  const isTerminalListProcessing =
    fetchState[GET_TERMINALS_ACTIVATION_ID] !== null &&
    fetchState[GET_TERMINALS_ACTIVATION_ID] === FetchStatus.PROCESSING;
  const isTerminalListProcessingFail =
    fetchState[GET_TERMINALS_ACTIVATION_ID] !== null &&
    fetchState[GET_TERMINALS_ACTIVATION_ID] === FetchStatus.FAILED;
  const isTerminalListProcessingUntreated =
    fetchState[GET_TERMINALS_ACTIVATION_ID] !== null &&
    fetchState[GET_TERMINALS_ACTIVATION_ID] === FetchStatus.UNTREATED;

  return {
    board: boardState,
    glossary: Glossary.instance(glState),
    isTerminalListProcessing: isTerminalListProcessing,
    isTerminalListProcessingFail: isTerminalListProcessingFail,
    isTerminalListProcessingUntreated: isTerminalListProcessingUntreated,
    terminal: terminalState,
    purchase: purchaseState
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _board = state.board;
  const _terminal = state.terminal;
  const _purchase = state.purchase;
  /** TerminalBoardの表示有無 */
  const _isDisplay =
    _board.status === BoardStatus.TERMINAL_DISP ||
    _board.status === BoardStatus.TERMINAL_CONFIRMATION_DISP ||
    _board.status === BoardStatus.TERMINAL_SUCCESS_DISP ||
    _board.status === BoardStatus.TERMINAL_FAILURE_DISP;
  /** 選択中のユーザー注文番号に紐づく端末情報のマップを取得 */
  const terminalMap: TerminalMap =
    _terminal[_board.selectedPurchaseIdForTerminal];
  /** 選択中のユーザー注文番号に紐づく購入情報を取得 */
  const purchaseInfo: PurchaseInformation =
    _purchase.purchasePlanList[_board.selectedPurchaseIdForTerminal];
  /** 端末リストマップを作成 */
  const _terminalMap: {
    [key: string]: {
      id: string;
      name: string;
      isDelete: boolean;
    }[];
  } = {};
  if (terminalMap != null) {
    for (const terminalId of Object.keys(terminalMap)) {
      const index = terminalMap[terminalId].terminalInfo.index;
      if (index in _terminalMap) {
        // 既に対応するデバイス利用状況のインデックス情報が存在する
        _terminalMap[index].push({
          id: terminalId,
          name: terminalMap[terminalId].terminalInfo.terminalName,
          isDelete: terminalMap[terminalId].isDelete
        });
      } else {
        // 対応するデバイス利用状況のインデックスが存在しない
        _terminalMap[index] = [
          {
            id: terminalId,
            name: terminalMap[terminalId].terminalInfo.terminalName,
            isDelete: terminalMap[terminalId].isDelete
          }
        ];
      }
    }
  }

  // 購入情報から取り出す値の初期値を設定
  let planLimit = 0;
  let planUsage = 0;
  // 購入情報が取得出来ていれば、取得した購入情報の値で更新
  if (purchaseInfo != null) {
    planLimit = purchaseInfo.planLimit ? purchaseInfo.planLimit : 0;
    // 使用数は端末削除後に購入情報を取得し直さないため、
    // 端末リストマップから算出する
    for (const id of Object.keys(_terminalMap)) {
      planUsage += _terminalMap[id].length;
    }
  }

  /** クローズボタンをクリックした際の処理 */
  const onClickClose = () => {
    dispatch(setStatus(BoardStatus.NOT_DISP));
  };
  /** 削除ボタンをクリックした際の処理 */
  const onClickDelete = () => {
    dispatch(setStatus(BoardStatus.TERMINAL_CONFIRMATION_DISP));
  };

  /** 削除対象フラグ更新処理 */
  const updateDelete = (terminalId: string, isDelete: boolean) => {
    dispatch(
      changeSetting(_board.selectedPurchaseIdForTerminal, terminalId, isDelete)
    );
  };

  return {
    isDisplay: _isDisplay,
    glossary: state.glossary,
    planLimit: planLimit,
    planUsage: planUsage,
    isTerminalListProcessing: state.isTerminalListProcessing,
    isTerminalListProcessingFail: state.isTerminalListProcessingFail,
    isTerminalListProcessingUntreated: state.isTerminalListProcessingUntreated,
    terminalMap: _terminalMap,
    onClickClose: onClickClose,
    onClickDelete: onClickDelete,
    updateDelete: updateDelete
  };
};

/**
 * 端末管理画面全体のContainer Component
 */
export default function ContainerComponent(ownProps: { isMobile?: boolean }) {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  return <TerminalBoard {...{ ..._props, ...ownProps }} />;
}
